import {
  Box,
  Spinner,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaAgreementStepDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { contractTypesToOptions } from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import { A3InnuvaWizardData } from 'modules/payroll_integrations_a3innuva/lib/types'

import { AgreementByLegalEntity } from '../Agreement'

const AgreementStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()

  const { data: agreementStepData, loading } = useQuery(A3InnuvaAgreementStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      organizationalUnitId: data.answers.organizational_unit_id,
    },
  })

  const contract_templates = agreementStepData?.contracts.contractTemplatesConnection.nodes ?? []
  const a3innuvaContractTypes = agreementStepData?.payrollIntegrationsA3innuva.contractTypes ?? []
  const options = contractTypesToOptions(a3innuvaContractTypes)

  useOnNext(() => {
    const contractTypes = Object.keys(data.answers).filter((key) =>
      key.startsWith('contract_type_')
    )
    const contractTypesCount = contractTypes.map((key) => !!data.answers[key]).length

    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsNumberOfContractTypesMapped, {
      company_id: session.company.id,
      access_id: session.access.id,
      contract_types_mapped: contractTypesCount,
    })
  })

  const onAgreementChange = (agreementId: string, value: string | number) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`contract_type_${agreementId}`]: value.toString(),
      },
    })
  }

  if (loading) return <Spinner />

  return (
    <Box gap="s24" width="s600">
      <Title
        title={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.agreement_level.title'
        )}
        description={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.agreement_level.description'
        )}
      />
      <Text.Normal>
        {i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.agreement_level.description_2'
        )}
      </Text.Normal>

      {contract_templates.map((contractTemplate) => {
        const contractTypes = contractTemplate.spanishContractTypeConnection.nodes ?? []
        if (!contractTypes || contractTypes.length === 0) return null

        return (
          <AgreementByLegalEntity
            data={data}
            key={`contract_template_${contractTemplate.id}_agreements`}
            name={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.agreement_level.factorial_value'
            )}
            agreements={contractTypes}
            onSelect={(agreementId, value, _label) => onAgreementChange(agreementId, value)}
            options={options}
          />
        )
      })}
    </Box>
  )
}

export const agreementStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.agreement_level.wizard_title'
  ),
  nextText: i18n.t('common.save'),
  render: (props) => <AgreementStep {...props} />,
  isCompleted: (_data) => true,
})
